import React, { useEffect, useState } from "react";
import { sunboundPayProxy } from '@proxies';
import { useSpinnerContext } from '@contexts';
import { Constants, Enums } from "@shared";
import currency from "currency.js";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import { DateTime } from "luxon";
import { TransactionBadge } from "@common";
import { useSessionContext } from '@contexts';
import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";

const PaymentHistory = () => {

    const { setSpinnerShow } = useSpinnerContext();
    const [invoices, setInvoices] = useState([]);
    const { session: { idAccount } } = useSessionContext();
    const [errorDownloadInvoice, setErrorDownloadInvoice] = useState(null);

    const errorDownload = <>File not found. Please contact <a href={`mailto:${Constants.Help.Email}`} className="underline">support</a> if needed</>;

    useEffect(() => {
        (async () => {
            setSpinnerShow(true);
            try {
                const response = await sunboundPayProxy.getPayments(idAccount);
                if (response && response.length !== 0)
                    response[0].open = true;
                setInvoices(response);
            } catch (error) {
                console.error(error);
            }
            setSpinnerShow(false);
        })();
    }, []);

    const toggle = (index) => {
        const newHistory = [...invoices];
        newHistory[index].open = !newHistory[index].open;
        setInvoices(newHistory);
    };

    const download = async (idInvoice) => {

        try {
            setSpinnerShow(true);
            const response = await sunboundPayProxy.downloadInvoice(idAccount, idInvoice);
            const { file, fileName } = response;
            const url = window.URL.createObjectURL(file);
            const link = document.createElement(`a`);
            link.href = url;
            link.setAttribute(`download`, fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            setErrorDownloadInvoice(idInvoice);
        } finally {
            setSpinnerShow(false);
        }
    };

    return (
        <div className="page-content-wrapper">
            <div className="content-box items-start">
                <>
                    {
                        !invoices || invoices.length === 0 &&
                        <span>No payments</span>
                    }
                    {

                        invoices.map((invoice, index) => {

                            const {
                                idInvoice,
                                dueDate,
                                invitations,
                                total: invoiceTotal,
                                totaTxFees,
                                totalPaid,
                                feeTotal: POTFee,
                                hasFile
                            } = invoice;
                            const txFee = currency(totaTxFees);
                            const paid = currency(totalPaid);
                            const invTotal = currency(invoiceTotal);
                            const potFee = currency(POTFee);
                            const granTotal = invTotal.add(txFee).add(potFee);
                            return (
                                <div key={idInvoice} className="flex flex-col shadow-sm border-2 rounded-xl bg-white p-4 w-full gap-y-3">
                                    <div className="flex flex-row justify-between">
                                        <span className="text-2xl font-bold">{DateTime.fromISO(dueDate).toFormat(`LLLL yyyy`)}</span>
                                        {
                                            invoice.open &&
                                            <button type="button" onClick={() => toggle(index)}>
                                                <ChevronUpIcon className="h-8 text-blue-text" />
                                            </button>
                                        }
                                        {
                                            !invoice.open &&
                                            <button type="button" onClick={() => toggle(index)}>
                                                <ChevronDownIcon className="h-8 text-blue-text" />
                                            </button>
                                        }
                                    </div>
                                    {
                                        invoice.open &&
                                        <>
                                            <div className="flex flex-col gap-y-1">
                                                <div className="flex flex-row font-semibold text-xl justify-between">
                                                    <span>Total</span>
                                                    <span>{granTotal.format()}</span>
                                                </div>
                                                <div className="flex flex-row justify-between">
                                                    <span>Community Charges</span>
                                                    <span>{invTotal.format()}</span>
                                                </div>
                                                {
                                                    potFee.intValue !== 0 &&
                                                    <div className="flex flex-row justify-between">
                                                        <span>Pay Over Time Fee</span>
                                                        <span>{potFee.format()}</span>
                                                    </div>
                                                }
                                                <div className="flex flex-row justify-between">
                                                    <span>Credit Card / ACH Fees</span>
                                                    <span>{txFee.format()}</span>
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-y-1">
                                                <div className="flex flex-row font-semibold text-lg justify-between">
                                                    <span>Payments</span>
                                                    <span>{paid.add(txFee).format()}</span>
                                                </div>
                                                {

                                                    invitations.map(invitation =>
                                                        invitation.payments.map(payment => {
                                                            const {
                                                                createdAt,
                                                                idPayment,
                                                                isAutopayment,
                                                                amount,
                                                                idPaymentType,
                                                                processingFeeAmount,
                                                                idTransactionStatus
                                                            } = payment;
                                                            const displayDate = DateTime.fromISO(createdAt).toFormat(`DD`);
                                                            const total = currency(amount).add(processingFeeAmount);
                                                            return (
                                                                <React.Fragment key={idPayment}>
                                                                    <div className="flex flex-row justify-between">
                                                                        <div className="flex gap-x-3">
                                                                            <span>{displayDate}</span>
                                                                            <span>{invitation.fullName}</span>
                                                                            <span>{Enums.PaymentType[idPaymentType]}</span>
                                                                            <TransactionBadge idTransactionStatus={idTransactionStatus} isAutopay={isAutopayment} />
                                                                        </div>
                                                                        <span>{total.format()}</span>
                                                                    </div>
                                                                </React.Fragment>
                                                            );
                                                        })
                                                    )
                                                }
                                            </div>
                                            {
                                                Boolean(hasFile) &&
                                                <>
                                                    <div className="flex flex-row justify-between">
                                                        <div className="text-blue-text text-xl">
                                                            Download Detailed Statement
                                                        </div>
                                                        <div>
                                                            <button onClick={() => download(idInvoice)}
                                                                className="background-transparent text-blue-text outline-none text-sm focus:outline-none">
                                                                <ArrowDownTrayIcon className="text-blue-text h-6 font-bold" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {
                                                        errorDownloadInvoice === idInvoice &&
                                                        <span className="text-red-500">{errorDownload}</span>
                                                    }
                                                    <div className="text-sm">
                                                        Charges in Sunbound may differ from this statement due to updates to your bill, processing fees, or if you’ve opted into Pay Over Time
                                                    </div>
                                                </>
                                            }
                                        </>
                                    }
                                </div>
                            );
                        })
                    }
                </>
            </div>
        </div>
    );
};

export default PaymentHistory;